import { TableProps } from '@insight-health-ai/roseate/components';
import { Clock } from '@insight-health-ai/roseate/icons';
import { TagsOutlined } from '@ant-design/icons';
import { Call, Tag } from 'src/models';
import { format, isSameYear, isToday } from 'date-fns';
import { formatPhoneNumber } from 'src/utils';
import { Tooltip, Typography } from 'antd';
import { UseGetCallListParams } from 'src/hooks/call';
import {
  CallPriority,
  CallStatus,
  CopyToClipboard,
  InsightTag,
  TagSelector,
} from 'src/components';
import { CallPractitioner } from './callPractitioner';
import { Actions } from './actions';

export const sortDirectionMap = {
  ascend: 'asc',
  descend: 'desc',
} as const;

export const UnreadIcon = ({
  showTooltip = true,
}: {
  showTooltip?: boolean;
}) => {
  return (
    <Tooltip title={showTooltip && 'Unread'}>
      <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
    </Tooltip>
  );
};

export const defaultFilters = {
  sortBy: 'start_time',
  sortDirection: 'desc',
} satisfies UseGetCallListParams;

const { Text } = Typography;

export const getColumns = ({
  filters,
  onTagChange,
}: {
  filters: UseGetCallListParams;
  onTagChange: (record: Call, tag: Tag, action: 'add' | 'remove') => void;
}) => {
  return [
    {
      title: '',
      key: 'is_read',
      dataIndex: 'is_read',
      render: (isRead: Call['is_read']) => {
        return (
          <div className="flex items-center justify-center flex-shrink-0">
            {!isRead ? <UnreadIcon /> : null}
          </div>
        );
      },
    },
    {
      title: 'Caller',
      dataIndex: 'source',
      key: 'source',
      onCell: () => ({
        style: {
          width: 'fit-content',
        },
      }),
      render: (source: string, record: Call) => {
        let durationStr = null;
        if (typeof record.duration === 'number' && record.duration > 0) {
          const mins = Math.floor(record.duration / 60);
          const secs = record.duration % 60;
          durationStr = `${mins}min ${secs}s`;
        }
        return (
          <Tooltip
            placement="topLeft"
            title={
              <div className="flex flex-col gap-2">
                <CopyToClipboard
                  text={record.call_id || ''}
                  copyTooltip={{
                    before: 'Copy Call ID',
                    after: 'Call ID copied!',
                  }}
                >
                  <span className="font-medium">Call ID</span>
                  <span className="text-gray-600">{record.call_id}</span>
                </CopyToClipboard>
                {record.external_id && (
                  <CopyToClipboard
                    text={record.external_id || ''}
                    copyTooltip={{
                      before: 'Copy External ID',
                      after: 'External ID copied!',
                    }}
                  >
                    <span className="font-medium">External ID</span>
                    <span className="text-gray-600">{record.external_id}</span>
                  </CopyToClipboard>
                )}
              </div>
            }
          >
            <div className="flex flex-col gap-1 text-nowrap">
              {source ? (
                <span>{formatPhoneNumber(source)}</span>
              ) : (
                <span className="text-gray-600 italic">Unknown</span>
              )}
              {durationStr && (
                <div className="flex items-center text-xs gap-1">
                  <Clock />
                  <span className="text-gray-600">{durationStr}</span>
                </div>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Time',
      key: 'start_time',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (_: any, record: Call) => {
        if (!record.start_time) {
          return <span>N/A</span>;
        }

        const today = isToday(new Date(record.start_time));
        const dateStr = isSameYear(new Date(record.start_time), new Date())
          ? format(new Date(record.start_time), 'MMMM dd')
          : format(new Date(record.start_time), 'MMMM dd, yyyy');
        const timeStr = format(new Date(record.start_time), 'hh:mm a');

        return (
          <div className="flex flex-col gap-1">
            <span className="text-nowrap">{today ? timeStr : dateStr}</span>
            {!today && <span className="text-xs text-gray-600">{timeStr}</span>}
          </div>
        );
      },
    },
    {
      title: 'Reason',
      dataIndex: 'encounter',
      key: 'encounter',
      render: (encounter: Call['encounter'], { agent }: Call) => (
        <div className="flex flex-col gap-1">
          {encounter?.description ? (
            <Text>{encounter?.description}</Text>
          ) : (
            <Text className="text-gray-600 italic">Unknown</Text>
          )}
          {agent?.name && (
            <Text className="text-xs text-gray-600">{agent?.name}</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      onCell: () => ({
        style: {
          maxWidth: 250,
          cursor: 'default',
        },
        onClick: (e: React.MouseEvent<HTMLTableCellElement>) => {
          e.stopPropagation();
        },
      }),
      render: (tags: Call['tags'], record: Call) => {
        if (!tags) {
          return '';
        }

        const wrapList = tags.length > 2;

        const tagsToRender = wrapList ? tags.slice(0, 2) : tags;

        let wrappedJsx = null;
        if (wrapList) {
          wrappedJsx = (
            <Tooltip
              title={
                <div className="flex flex-col gap-4 p-2 max-w-[300px]">
                  <div className="text-sm font-medium flex items-center gap-2">
                    <TagsOutlined /> {tags.length} Tags
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tags?.map((tag) => (
                      <InsightTag
                        key={tag.id}
                        tag={tag}
                        isSelected
                        onClose={() => {
                          onTagChange(record, tag, 'remove');
                        }}
                      />
                    ))}
                  </div>
                </div>
              }
            >
              <span className="text-sm">
                +{tags.length - tagsToRender.length}
              </span>
            </Tooltip>
          );
        }
        return (
          <div className="flex gap-2 flex-wrap">
            {tagsToRender?.map((tag) => (
              <InsightTag
                key={tag.id}
                tag={tag}
                isSelected
                onClose={() => {
                  onTagChange(record, tag, 'remove');
                }}
              />
            ))}
            {wrappedJsx}
            {record.encounter_id && (
              <TagSelector
                selectedTags={tags}
                onAdd={async (tag) => {
                  await onTagChange(record, tag, 'add');
                }}
                onRemove={async (tag) => {
                  await onTagChange(record, tag, 'remove');
                }}
              />
            )}
          </div>
        );
      },
    },
    {
      title: 'Assigned To',
      dataIndex: 'assigned_to_practitioner',
      key: 'assigned_to_practitioner',
      onCell: () => ({
        style: {
          cursor: 'default',
        },
        onClick: (e: React.MouseEvent<HTMLTableCellElement>) => {
          e.stopPropagation();
        },
      }),
      render: (_: unknown, record: Call) => {
        return <CallPractitioner call={record} />;
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (priority: Call['priority']) => {
        return <CallPriority priority={priority} />;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      onCell: () => ({
        style: {
          width: 'fit-content',
        },
      }),
      render: (status: Call['status']) => {
        return <CallStatus status={status} />;
      },
    },
    {
      title: '',
      key: 'actions',
      // fixed: 'right' as const,
      onCell: () => {
        return {
          onClick: (e: React.MouseEvent<HTMLTableCellElement>) => {
            e.stopPropagation();
          },
          style: {
            cursor: 'default',
            width: 50,
            // ...(!record.is_read
            //   ? { backgroundColor: theme.colors.yellow['50'] }
            //   : {}),
          },
        };
      },
      render: (_: unknown, record: Call) => {
        return <Actions call={record} />;
      },
    },
  ].map((column) => {
    if ((column as any).key === filters.sortBy) {
      return {
        ...column,
        defaultSortOrder:
          filters.sortDirection === 'desc' ? 'descend' : 'ascend',
      };
    }

    return column;
  }) as TableProps<Call>['columns'];
};

export const hasFiltersApplied = (filters: UseGetCallListParams) => {
  const { sortBy, sortDirection, ...rest } = filters;
  return Object.values(rest).some((value) => value !== undefined);
};
